import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useOldMovies } from '../hooks/useOldMovies';
import MovieCard from '../components/MovieCard';
import Pagination from '../components/Pagination';
import { FormattedMovie } from '../types/movie';

interface MoviesByMonth {
  [key: string]: FormattedMovie[];
}

const OldMovies: React.FC = () => {
  const {
    movies,
    loading,
    error,
    currentPage,
    totalCount,
    totalPages,
    goToPage,
    refresh
  } = useOldMovies(24); // 24 movies per page

  // Group movies by month and year
  const moviesByMonth = useMemo(() => {
    const grouped: MoviesByMonth = {};
    
    movies.forEach(movie => {
      const date = new Date(movie.releaseDate);
      const monthYear = date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long'
      });
      
      if (!grouped[monthYear]) {
        grouped[monthYear] = [];
      }
      grouped[monthYear].push(movie);
    });
    
    return grouped;
  }, [movies]);

  if (error) {
    return (
      <div className="container-custom py-20">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error Loading Movies</h2>
          <p className="text-gray-600 mb-4">{error.message}</p>
          <button
            onClick={refresh}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-background-light">
      <Helmet>
        <title>Movie Archive - Next in Cinema</title>
        <meta name="description" content="Browse our complete archive of movies organized by release date. Discover films from previous months and years." />
        <meta property="og:title" content="Movie Archive - Next in Cinema" />
        <meta property="og:description" content="Browse our complete archive of movies organized by release date. Discover films from previous months and years." />
        <meta property="og:url" content="https://www.nextincinema.com/archive" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://www.nextincinema.com/archive" />
      </Helmet>

      {/* Archive Hero Section */}
      <div className="relative h-[40vh] md:h-[50vh] min-h-[280px] flex items-center pt-16">
        {/* Background Image with Gradient Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1489599904472-84978f312dd2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"
            alt="Cinema Archive Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-dark/90 via-background-dark/60 to-background-dark/30" />
        </div>

        {/* Content */}
        <div className="container-custom relative z-20 px-4 md:px-6">
          <div className="text-center animate-fade-in">
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-white/90 text-shadow-sm mb-4">
              Movie Archive
            </h1>
            <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto mb-6">
              Browse our complete collection of movies organized by release date.
              Discover films from previous months and explore cinema history.
            </p>
            <div className="flex items-center justify-center space-x-4 text-sm text-white/70">
              <Link to="/" className="hover:text-accent-teal transition-colors duration-200">
                ← Back to Home
              </Link>
              <span>•</span>
              <span>{totalCount} movies in archive</span>
            </div>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute bottom-0 left-0 right-0 h-12 md:h-16 bg-gradient-to-t from-background-light to-transparent z-10" />
      </div>

      <div className="container-custom py-12">

        {/* Loading State */}
        {loading && movies.length === 0 && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-secondary">Loading movies...</p>
          </div>
        )}

        {/* Movies Grid */}
        {!loading && movies.length === 0 ? (
          <div className="text-center py-12">
            <h3 className="text-xl font-semibold text-secondary-dark mb-2">No Movies Found</h3>
            <p className="text-secondary">There are no movies in the archive yet.</p>
          </div>
        ) : (
          <div className="space-y-12">
            {Object.entries(moviesByMonth).map(([monthYear, monthMovies]) => (
              <section key={monthYear} className="space-y-6">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-secondary-dark border-b-2 border-blue-600 pb-2">
                  {monthYear}
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-6">
                  {monthMovies.map((movie) => (
                    <MovieCard
                      key={movie._id}
                      id={movie._id}
                      poster={movie.poster}
                      title={movie.title}
                      releaseDate={movie.releaseDate}
                      hypeScore={movie.hypeScore}
                      slug={movie.slug}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}

        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={goToPage}
          loading={loading}
        />

        {/* Loading indicator for pagination */}
        {loading && movies.length > 0 && (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-sm text-secondary">Loading...</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="container-custom py-8 border-t border-secondary/10">
        <p className="text-secondary text-sm text-center">
          Posters and promotional materials are used under fair use for promotional purposes. 
          All images are © their respective copyright holders, including Disney, Warner Bros., and Universal Pictures.
        </p>
      </footer>
    </main>
  );
};

export default OldMovies;